import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';
import {
  CategoryName,
  DisplayModeName,
  TDatabaseId,
} from '@infotech-soe/metaschema';

import IApiProvider, {
  DatasetParamValues,
} from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';

import IHierarchyService, { MoveHierarchyRequest } from './IHierarchyService';

export class HierarchyServiceSingleton implements IHierarchyService {
  private readonly apiProvider: IApiProvider;
  constructor() {
    this.apiProvider = new MerpsApiProvider();
  }

  public loadRoots(
    category: CategoryName,
    displayMode: DisplayModeName,
    params?: DatasetParamValues
  ): Promise<ITreeNodeInstance[]> {
    return this.treeApi.get(null, category, displayMode, params || {});
  }

  public loadChildren(
    category: CategoryName,
    displayMode: DisplayModeName,
    parentId: TDatabaseId,
    params: DatasetParamValues
  ): Promise<ITreeNodeInstance[]> {
    return this.treeApi.get(
      parentId as TDatabaseId,
      category,
      displayMode,
      params
    );
  }

  public getPath(
    category: CategoryName,
    displayMode: DisplayModeName,
    nodeId: TDatabaseId
  ): Promise<TDatabaseId[]> {
    return this.treeApi.getPath(nodeId as TDatabaseId, category, displayMode);
  }

  public async getRecursiveChildIds(
    category: CategoryName,
    displayMode: DisplayModeName,
    parentId: TDatabaseId,
    params: DatasetParamValues
  ): Promise<TDatabaseId[]> {
    const children = await this.treeApi.getRecursive(
      parentId as TDatabaseId,
      category,
      displayMode,
      params
    );
    return children.map(child => child.id);
  }

  public moveHierarchy(
    categoryName: CategoryName,
    request: MoveHierarchyRequest
  ): Promise<void> {
    return this.apiProvider.rpc.callMethod('queries', 'documentMove', [
      categoryName,
      request.id as TDatabaseId,
      request.pid as TDatabaseId,
    ]);
  }

  private get treeApi() {
    return this.apiProvider.tree;
  }
}

const HierarchyService = (() => {
  let instance: IHierarchyService;
  return new Proxy(HierarchyServiceSingleton, {
    construct(Target: typeof HierarchyServiceSingleton): IHierarchyService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default HierarchyService;
